import { useSelector, shallowEqual } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AbandonedCart, AbandonedCartStatus, Product } from 'state/abandoned-cart/types/abandoned-cart';
import { selectEncryptedQuoteId, selectData } from 'state/abandoned-cart/abandoned-cart-slice';
import { selectDetails } from 'state/account/account-slice';
import { selectIsLoadingPatch } from 'state/abandoned-cart/abandoned-cart-slice';

export enum AbandonedCartUpdateStep {
  SELECT_TARIFF = 'SELECT_TARIFF',
  PERSONAL_DETAILS = 'PERSONAL_DETAILS',
  CONFIRMATION = 'CONFIRMATION',
  RETURNED = 'RETURNED'
}

const getPayloadStatus = (step: AbandonedCartUpdateStep, currentStatus: AbandonedCartStatus): AbandonedCartStatus => {
  switch (true) {
    case step === AbandonedCartUpdateStep.RETURNED:
      return AbandonedCartStatus.RETURNED;
    case step == AbandonedCartUpdateStep.CONFIRMATION && currentStatus === AbandonedCartStatus.RETURNINPROGRESS:
      return AbandonedCartStatus.RETURNCOMPLETE;
    case step == AbandonedCartUpdateStep.CONFIRMATION && currentStatus !== AbandonedCartStatus.RETURNINPROGRESS:
      return AbandonedCartStatus.COMPLETE;
    case currentStatus === AbandonedCartStatus.RETURNED:
      return AbandonedCartStatus.RETURNINPROGRESS;
    default:
      return currentStatus;
  }
};

export const useAbandonedCartUpdate = (
  step: AbandonedCartUpdateStep,
) : {
  isAbandonedCartAvailable: boolean;
  payload: AbandonedCart;
  buildPayload: (product: Product) => AbandonedCart;
  encryptedQuoteId: string;
} => {
  const { releaseAbandonedCart } = useFlags();

  const isLoading = useSelector(selectIsLoadingPatch, shallowEqual);
  const encryptedQuoteId = useSelector(selectEncryptedQuoteId, shallowEqual);
  const abandonedCartData = useSelector(selectData, shallowEqual);
  const { firstName, lastName, phoneNumber } = useSelector(selectDetails, shallowEqual);

  const isAbandonedCartAvailable =
    !!releaseAbandonedCart &&
    !!encryptedQuoteId &&
    !isLoading;

  const status = getPayloadStatus(step, abandonedCartData.status);

  const payload = {
    status: status,
    ...(step === AbandonedCartUpdateStep.PERSONAL_DETAILS && {
      customerDetails:
        {
          firstName: firstName,
          lastName: lastName,
          emailAddress: abandonedCartData?.customerDetails?.emailAddress,
          phoneNumber: phoneNumber
        }
    } )
  }

  const buildPayload = (product: Product): AbandonedCart => ({
    ...payload,
    ...(product && {
      products: [
        {
          code: product.code,
          name: product.name,
          annualAmount: product.annualAmount,
          monthlyAmount: product.monthlyAmount
        }
      ]
    }),
    updatedTimeStamp: undefined,
    createdTimeStamp: undefined
  });

  return {
    isAbandonedCartAvailable,
    payload,
    buildPayload,
    encryptedQuoteId
  }
};
